import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Collapse,
  Paper,
  Stack,
  Chip,
  Pagination,
} from "@mui/material";
import { tokens } from "../../../theme";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import DataTable from "../../../components/SüperAdminComponents/DataTable";
import PeopleIcon from "@mui/icons-material/People";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function DoctorsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    doctorName: "",
    email: "",
    role: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminService.getAllDoctors({
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setDoctors(response.data || []);
      setTotalCount(Number(response.headers?.["x-total-count"] || 0));
      setTotalPages(Number(response.headers?.["x-total-pages"] || 1));
    } catch (error) {
      console.error("Error fetching doctors:", error);
      setSnackbar({
        open: true,
        message: "Error fetching doctors",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleView = async (doctor) => {
    try {
      const detailedDoctor = await superAdminService.getDoctorById(doctor.id);
      setSelectedDoctor(detailedDoctor);
      setViewDialogOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error fetching doctor details",
        severity: "error",
      });
    }
  };

  const columns = [
    { id: "name", label: "Name" },
    { id: "surname", label: "Surname" },
    { id: "email", label: "Email" },
    { id: "role", label: "Role" },
    { id: "specialization", label: "Specialization" },
    {
      id: "Tenant",
      label: "Tenant",
      format: (value) => value?.name || "-",
    },
  ];

  const sanitizeFilters = useMemo(() => () => {
    const output = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) output[key] = value;
    });
    return output;
  }, [filters]);

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(sanitizeFilters());
  };

  const resetFilters = () => {
    setFilters({
      doctorName: "",
      email: "",
      role: "",
      dateFrom: "",
      dateTo: "",
    });
    setPageIndex(0);
    setAppliedFilters({});
  };

  const activeFilterCount = Object.keys(appliedFilters || {}).length;

  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers?.["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || `doctors-export-${Date.now()}.xlsx`;
  };

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const response = await superAdminService.exportExcel({
        resource: "doctors",
        filters: appliedFilters,
        sortBy,
        sortOrder,
      });
      const fileName = getFilenameFromHeaders(response.headers);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Excel downloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Excel export failed", severity: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: 3, 
      width: "100%", 
      minWidth: "100%", 
      maxWidth: "100%", 
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3,width: "100%",minWidth: "100%" }}>
        <PeopleIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Doctors Management
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              endIcon={filtersOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              onClick={() => setFiltersOpen((prev) => !prev)}
              sx={{
                color: colors.grey[100],
                backgroundColor: filtersOpen ? colors.blueAccent[700] : colors.primary[300],
                "&:hover": {
                  backgroundColor: filtersOpen ? colors.blueAccent[800] : colors.primary[200],
                },
              }}
            >
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExcelExport}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export Excel"}
            </Button>
          </Stack>
        </Box>
      </Box>
      <Collapse in={filtersOpen} timeout={250} unmountOnExit>
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 1.5 }}>
            <TextField
              label="Doctor Name"
              size="small"
              value={filters.doctorName}
              onChange={(event) => setFilters((prev) => ({ ...prev, doctorName: event.target.value }))}
            />
            <TextField
              label="Email"
              size="small"
              value={filters.email}
              onChange={(event) => setFilters((prev) => ({ ...prev, email: event.target.value }))}
            />
            <TextField
              select
              label="Role"
              size="small"
              value={filters.role}
              onChange={(event) => setFilters((prev) => ({ ...prev, role: event.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="doctor">Doctor</MenuItem>
            </TextField>
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={filters.dateFrom}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date To"
              type="date"
              size="small"
              value={filters.dateTo}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>Clear</Button>
          </Stack>
        </Paper>
      </Collapse>

      <DataTable
        columns={columns}
        data={doctors}
        onView={handleView}
        loading={loading}
        emptyMessage="No doctors found"
      />
      <Paper elevation={0} sx={{ mt: 2, p: 1.5, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`Total ${totalCount}`} />
            <Chip size="small" label={`Page ${pageIndex + 1}/${totalPages}`} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField select label="Rows" size="small" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPageIndex(0); }} sx={{ minWidth: 90 }}>
              {[10, 20, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
            <TextField select label="Sort By" size="small" value={sortBy} onChange={(event) => { setSortBy(event.target.value); setPageIndex(0); }} sx={{ minWidth: 120 }}>
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </TextField>
            <TextField select label="Direction" size="small" value={sortOrder} onChange={(event) => { setSortOrder(event.target.value); setPageIndex(0); }} sx={{ minWidth: 110 }}>
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </Stack>
          <Pagination count={Math.max(totalPages, 1)} page={Math.min(pageIndex + 1, Math.max(totalPages, 1))} onChange={(_, page) => setPageIndex(page - 1)} shape="rounded" />
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100] }}>
          Doctor Details
        </DialogTitle>
        <DialogContent>
          {selectedDoctor && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Name
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedDoctor.name} {selectedDoctor.surname}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedDoctor.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Role
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedDoctor.role}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Specialization
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedDoctor.specialization || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedDoctor.phone_number || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                  Tenant
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  {selectedDoctor.Tenant?.name || "-"}
                </Typography>
              </Grid>
              {selectedDoctor.createdDoctors && selectedDoctor.createdDoctors.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: colors.grey[400], mb: 1 }}>
                    Created Doctors ({selectedDoctor.createdDoctors.length})
                  </Typography>
                  {selectedDoctor.createdDoctors.map((doctor) => (
                    <Box
                      key={doctor.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: "8px",
                        backgroundColor: colors.primary[600],
                        border: `1px solid ${colors.grey[800]}30`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                        {doctor.name} {doctor.surname} ({doctor.email})
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              )}
              {selectedDoctor.patients && selectedDoctor.patients.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: colors.grey[400], mb: 1 }}>
                    Patients ({selectedDoctor.patients.length})
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                    {selectedDoctor.patients.length} patient(s) assigned
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: colors.grey[100] }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

